'use client'
import { useState, useEffect } from 'react'
import { CreateExitMobileIcon } from 'Icons'
import CreateDetailsMobile from '@/components-mobile/create/CreateDetailsMobile'
import { useRouter } from 'next/navigation'
import useI18n from 'Hooks/useI18n'
import { withLocale, withoutLocale } from '@/locales'
import { EyeOutlined } from '@ant-design/icons'
import { useRecoilValue, useResetRecoilState } from 'recoil'
import { createStateFactory } from '@/store/create'
import { userState } from '@/store/user'
import { usePathname, useSearchParams } from 'next/navigation'
import PreviewDom from '@/components/create-preview/PreviewDom'
import {
  getLocalCharacterDraftCacheKey,
  saveLocalCharacterDraftCache,
} from '@/lib/draft/localCharacterDraftCache'

const STEP_LIST = ['Create Image', 'Profile Details', 'Conversation Settings']

export default function Create() {
  const { t, lang } = useI18n()
  const [step, setStep] = useState<number>(0)
  const [showPreview, setShowPreview] = useState(false)
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const isUpdateChar =
    withoutLocale(pathname) === '/update' ||
    searchParams.get('update') === 'true'
  const from = searchParams.get('from')
  const source = from || 'character_image'
  const createCharFormState = createStateFactory(false, source)
  const updateCharFormState = createStateFactory(true, source)
  const createCharForm = useRecoilValue(createCharFormState)
  const updateCharForm = useRecoilValue(updateCharFormState)
  const resetCreateCharForm = useResetRecoilState(createCharFormState)
  const resetUpdateCharForm = useResetRecoilState(updateCharFormState)
  const user = useRecoilValue(userState)
  const charForm = isUpdateChar ? updateCharForm : createCharForm
  const contentType = charForm.contentType
  const isRichDisplayContent = contentType === 2 || contentType === 3
  const previewTagline = isRichDisplayContent
    ? charForm.styledTagline
    : charForm.charShortDescribe
  const previewGreeting = isRichDisplayContent
    ? charForm.styledGreeting
    : charForm.charGreeting
  const characterId = searchParams.get('character_id')

  const handleBack = () => {
    if (isUpdateChar) {
      resetUpdateCharForm()
    } else {
      saveLocalCharacterDraftCache(
        getLocalCharacterDraftCacheKey({
          userId: user?.user_id,
          isUpdateChar,
          source,
          characterId,
        }),
        charForm,
        {
          userId: user?.user_id,
          isUpdateChar,
          source,
          characterId,
        }
      )
      resetCreateCharForm()
    }
    router.back()
  }

  // 获取安全区域
  const [safeAreaTop, setSafeAreaTop] = useState(0)
  const [safeAreaBottom, setSafeAreaBottom] = useState(0)

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const top = parseInt(
        getComputedStyle(document.documentElement).getPropertyValue(
          'env(safe-area-inset-top)'
        ) || '0'
      )
      const bottom = parseInt(
        getComputedStyle(document.documentElement).getPropertyValue(
          'env(safe-area-inset-bottom)'
        ) || '0'
      )
      setSafeAreaTop(top)
      setSafeAreaBottom(bottom)
    }
  }, [])

  // 默认主色调（如果没有从图片提取）
  const defaultPrimaryColor = '#9C4844'

  if (showPreview) {
    return (
      <div style={{ width: '100%', height: '100vh' }}>
        <PreviewDom
          characterName={charForm.charNickname || ''}
          characterFaceUrl={charForm.charFaceImagePreviewUrl || ''}
          characterImageUrl={charForm.charImageUrl || ''}
          userName={user?.nickname || ''}
          tagline={previewTagline || ''}
          greeting={previewGreeting || ''}
          globalCss={contentType === 2 ? charForm.optimizationCode || '' : ''}
          primaryColor={defaultPrimaryColor}
          safeAreaTop={safeAreaTop}
          safeAreaBottom={safeAreaBottom}
          onBack={() => setShowPreview(false)}
          isStyledMarkdown={contentType === 3}
        />
      </div>
    )
  }

  return (
    <main className="absolute inset-0 flex h-full w-full flex-col">
      <div className="pb-[env(safe-area-inset-top,0)]" />
      <div className="flex items-center justify-between px-2.5 pt-2.5">
        <div className="flex items-center">
          <div className="text-sm">{t('Create Character')}</div>
        </div>
        <div className="flex items-center gap-2">
          {contentType !== 1 && (
            <button
              type="button"
              className="flex items-center gap-1 text-sm text-white"
              onClick={() => {
                setShowPreview(true)
              }}
            >
              <EyeOutlined />
              <span>{t('Preview')}</span>
            </button>
          )}
          {contentType !== 1 && <div className="h-3 w-[1px] bg-white/50" />}
          <CreateExitMobileIcon onClick={handleBack} />
        </div>
      </div>
      <div className="flex-1 overflow-auto">
        <CreateDetailsMobile setStep={setStep} />
      </div>
    </main>
  )
}
